import type { ReploElement } from "schemas/generated/element";

import * as React from "react";

import { LEFT_BAR_MENU_WIDTH } from "@v1/components/canvas/canvas-constants";
import { V1CommandMenuItemsProvider } from "@v1/components/command-menu/V1CommandMenuItemsProvider";
import CurrentProjectV1Initializer from "@v1/components/CurrentProjectV1Initializer";
import { V1DebugLeftPanel } from "@v1/components/debug/V1DebugPanel";
import { ToastManager } from "@v1/components/designSystem/alert/Toast";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import {
  DEBUG_PANEL_WIDTH,
  RIGHT_BAR_WIDTH,
} from "@v1/components/editor/constants";
import { CustomFontLoader } from "@v1/components/editor/CustomFontLoader";
import FigmaImportPanel from "@v1/components/editor/page/element-editor/components/FigmaImportPanel";
import TemplateProductSelectorWrapper from "@v1/components/editor/page/element-editor/components/TemplateProductSelector";
import { SrcDocFontLoader } from "@v1/components/editor/SrcDocFontLoader";
import GlobalHotkeysListener from "@v1/components/GlobalHotkeysListener";
import ArchivedElementsPane from "@v1/components/modals/ArchivedElementsPane";
import { RichTextComponentProvider } from "@v1/components/RichTextComponentContext";
import { EditorPerformanceProvider } from "@v1/contexts/editor-performance.context";
import useFetchDraftElement from "@v1/hooks/useFetchDraftElement";
import useFetchMetafields from "@v1/hooks/useFetchMetafields";
import useRightBarVisibility from "@v1/hooks/useRightBarVisibility";
import { useDraftElementProductsIds } from "@v1/hooks/useStoreProducts";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import { DragAndDropProvider } from "@v1/providers/DragAndDropProvider";
import { selectDraftElementType } from "@v1/reducers/core-reducer";
import {
  selectLeftBarActiveTab,
  selectLeftBarWidth,
  setLeftBarActiveTab,
} from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { FullScreenLoader } from "@v2/components/FullScreenLoader";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useAdminPanelStore } from "@v2/features/admin/adminPanelStore";
import BackendStagingBanner from "@v2/features/debug/BackendStagingBanner";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useDocumentTitle } from "@v2/hooks/useDocumentTitle";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { docs } from "@v2/utils/docs";
import { routes } from "@v2/utils/router";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { useSyncDraftElementWithRouter } from "../hooks/useSyncDraftElementWithRouter";

const PRODUCTS_LIMIT = 20;
const elementTypeToLabel: Record<ReploElement["type"], string> = {
  page: "page",
  shopifyArticle: "article",
  shopifyProductTemplate: "product template",
  shopifySection: "Shopify section",
};

interface EditorAppProps {
  toRenderWithinEditor?: React.ReactNode;
  toRenderOverEditor?: React.ReactNode;
}

export function EditorApp({
  toRenderWithinEditor,
  toRenderOverEditor,
}: EditorAppProps) {
  const { isLoading: isUserLoading, isAuthenticated } = useCurrentUser();
  const {
    project,
    isLoading: isProjectLoading,
    error: projectError,
  } = useCurrentProjectContext();

  const location = useLocation();
  const isTemplateLibraryOpen = location.pathname.includes("/new");

  const [isUsingBackendStaging] = useLocalStorageState(
    "replo.debug.useBackendStaging",
    false,
    { schema: z.boolean() },
  );

  useFetchDraftElement();
  useSyncDraftElementWithRouter();
  useSettingsNavigationEffect();

  const errorToast = useV1ErrorToast();

  const isUserNotAuthenticated = !isUserLoading && !isAuthenticated;

  const projectNotFound = Boolean(
    !isProjectLoading && projectError?.meta.response?.status == 404,
  );

  useDocumentTitle(project?.name ?? "Dashboard");

  if (isUserNotAuthenticated) {
    return <LoginModal />;
  }

  if (projectNotFound) {
    errorToast(
      "Project not found",
      "Please refresh or reach out to support@replo.app if you think this is a mistake.",
      "error.project.not_found",
      { projectError },
    );
    return <Navigate to={routes.home.root} replace />;
  }

  return (
    <div id="app" className="canvas h-screen w-full">
      <SupportChatBubbleStyles />
      <EditorPerformanceProvider>
        <DragAndDropProvider>
          <CurrentProjectV1Initializer />
          <MetafieldsAutoFetcher />
          <SrcDocFontLoader />
          <CustomFontLoader />
          {(isUserLoading || isProjectLoading) && <FullScreenLoader />}
          <RichTextComponentProvider>
            {!isProjectLoading && toRenderWithinEditor}
            <V1CommandMenuItemsProvider />
            <GlobalHotkeysListener />
            <ToastWrapper />
            <div
              className="flex h-full w-full flex-col overflow-hidden"
              style={{ zIndex: 2_147_483_649 }}
            >
              <div className="flex w-full flex-1 overflow-hidden">
                {isUsingBackendStaging && <BackendStagingBanner />}
                <V1DebugLeftPanel />
                {toRenderOverEditor}
                {!isTemplateLibraryOpen && <TemplateProductSelectorWrapper />}
                <FigmaImportPanel />
                <GlobalModals />
                <ArchivedElementsPane />
              </div>
            </div>
          </RichTextComponentProvider>
        </DragAndDropProvider>
      </EditorPerformanceProvider>
    </div>
  );
}

function LoginModal() {
  const navigate = useNavigate();

  return (
    <div id="app" className="bg-selectable-selected h-screen w-full">
      <Modal
        onOpenChange={() => {}}
        isOpen={true}
        size="base"
        title="Your Replo session has expired. Please log in again."
        description="If you think this is a mistake, please reach out to us at support@replo.app"
        footer={
          <DeprecatedButton
            variant="primary"
            size="base"
            onClick={() => {
              navigate("/auth/login");
            }}
          >
            Log In
          </DeprecatedButton>
        }
      />
    </div>
  );
}

// Wrapper components to prevent unnecessary re-renders on EditorApp
function ToastWrapper() {
  const isRightBarVisible = useRightBarVisibility();
  const rightOffset = isRightBarVisible ? RIGHT_BAR_WIDTH : 0;
  return (
    <ToastManager
      containerStyle={rightOffset ? { right: rightOffset } : undefined}
    />
  );
}

/**
 * Component which registers hooks to auto-fetch metafield values when needed and
 * returns nothing. This is in its own component rather than in EditorApp because
 * we don't want changes in the products which we need to fetch metafields for to
 * have to rerender the entire React tree
 */
function MetafieldsAutoFetcher() {
  useFetchMetafields({ type: "variant" });
  useFetchMetafields({ type: "product" });
  return null;
}

function GlobalModals() {
  return <TooManyProductsModalWrapper />;
}

function TooManyProductsModalWrapper() {
  const elementType = useEditorSelector(selectDraftElementType);
  const readableElementType =
    elementTypeToLabel[elementType] ?? elementTypeToLabel["page"];
  const productIds = useDraftElementProductsIds();
  const productsCount = new Set(productIds.map((id) => String(id))).size;
  const [modalAlreadyShown, setModalAlreadyShown] = React.useState(false);
  const isOverLimit = productsCount > PRODUCTS_LIMIT;

  return (
    isOverLimit &&
    !modalAlreadyShown && (
      <Modal
        isOpen
        onOpenChange={() => {
          setModalAlreadyShown(true);
        }}
        size="sm"
        title="Too many Shopify products"
        description={`${productsCount}/${PRODUCTS_LIMIT} referenced products`}
        footer={
          <div className="flex flex-row gap-2">
            <DeprecatedButton
              variant="link"
              type="button"
              to={docs.shopifyMaxProductsOnPage}
              target="_blank"
              rel="noreferrer"
              size="base"
            >
              Learn More
            </DeprecatedButton>
            <DeprecatedButton
              variant="primary"
              type="button"
              size="base"
              onClick={() => {
                setModalAlreadyShown(true);
              }}
            >
              Continue
            </DeprecatedButton>
          </div>
        }
      >
        <p className="text-default text-xs">
          Your {readableElementType} references more than {PRODUCTS_LIMIT}{" "}
          products, which is a Shopify limit. You'll have to remove a product in
          order to publish successfully.
        </p>
      </Modal>
    )
  );
}

function useSettingsNavigationEffect() {
  const location = useLocation();
  const dispatch = useEditorDispatch();
  const logEvent = useLogAnalytics();

  React.useEffect(() => {
    const state = location.state as { openSettings?: boolean };

    if (state?.openSettings) {
      logEvent("editor.panel.toggle", {
        toggle: "settings",
        isOpened: true,
        openedWithShortcut: false,
      });
      dispatch(setLeftBarActiveTab("settings"));
      // Clear the navigation state after handling it
      window.history.replaceState({}, "");
    }
  }, [location.state, dispatch, logEvent]);
}

const SupportChatBubbleStyles = () => {
  const isLeftBarOpened = Boolean(useEditorSelector(selectLeftBarActiveTab));
  const leftBarWidth = useEditorSelector(selectLeftBarWidth); // Fetching the current width of the left bar
  const isDebugPanelVisible = useAdminPanelStore((s) => s.isVisible);

  let supportChatPosition = LEFT_BAR_MENU_WIDTH + 12;
  if (isLeftBarOpened) {
    supportChatPosition = supportChatPosition + leftBarWidth;
  }
  if (isDebugPanelVisible) {
    supportChatPosition = supportChatPosition + DEBUG_PANEL_WIDTH;
  }

  const editorStyles = `
    .PylonChat-bubbleFrameContainer {
      position: fixed;
      bottom: 16px;
      left: ${supportChatPosition}px;
    }

  `;

  return <style dangerouslySetInnerHTML={{ __html: editorStyles }} />;
};
